import React, { useState, useRef, useEffect } from 'react';

import { Droppable } from 'react-beautiful-dnd';

import SidePropertiesContainer from '../../../../SidePropertiesContainer/SidePropertiesContainer';
import SideProperties from '../../SideProperties/SideProperties';
import GroupAndRandomiseChildHeader from '../GroupAndRandomiseChildHeader/GroupAndRandomiseChildHeader';
import Icon from '../../../../../../Icon/Icon';

import groupAndRandomiseChildPlaceholderImage from '../../../../../../../../../../../assets/img/groupandrandomisechildplaceholder.png';

import styles from '../../../Group.module.css';

const SHUFFLE_ICON_HALF_HEIGHT = 15;

export default function({
  actions,
  isActiveGroup,
  group,
  state,
  surveyStructure,
  surveyListGroup,
  isGroupAndRandomiseChildGroup,
  groupNameRef,
  setGroupNameRef,
  elements
}) {
  const [expanded, setExpanded] = useState(true);
  const [shuffleIconTop, setShuffleIconTop] = useState(null);
  const elementsContainerRef = useRef(null);

  /*
  "Group and randomise": when the child group is randomised its questions are
  connected with lines (QuestionConnectionLine) that merge into a central
  line, with a shuffle icon on the vertical center between the first and the
  last question that take part in the randomisation (questions with a locked
  position are excluded).
  */
  const questions = (state && state.survey && state.survey.questions) || {};
  const lockedFlags = (group.flows || []).map(
    flowId =>
      !!(questions[flowId] && questions[flowId].groupAndRandomiseLockedPosition)
  );
  const firstUnlockedIndex = lockedFlags.indexOf(false);
  const lastUnlockedIndex = lockedFlags.lastIndexOf(false);
  const showShuffleIcon = !!(
    isGroupAndRandomiseChildGroup &&
    expanded &&
    group.randomized &&
    firstUnlockedIndex > -1 &&
    firstUnlockedIndex !== lastUnlockedIndex
  );

  useEffect(() => {
    if (!showShuffleIcon || !elementsContainerRef.current) {
      if (shuffleIconTop !== null) {
        setShuffleIconTop(null);
      }
      return;
    }

    const containerRect = elementsContainerRef.current.getBoundingClientRect();
    const getQuestionCenter = flowId => {
      const questionElement = elementsContainerRef.current.querySelector(
        `.question-${flowId}`
      );
      if (!questionElement) return null;

      const questionRect = questionElement.getBoundingClientRect();
      return questionRect.top - containerRect.top + questionRect.height / 2;
    };

    const firstCenter = getQuestionCenter(group.flows[firstUnlockedIndex]);
    const lastCenter = getQuestionCenter(group.flows[lastUnlockedIndex]);
    if (firstCenter === null || lastCenter === null) return;

    const iconTop = Math.round(
      firstCenter + (lastCenter - firstCenter) / 2 - SHUFFLE_ICON_HALF_HEIGHT
    );
    if (iconTop !== shuffleIconTop) {
      setShuffleIconTop(iconTop);
    }
  });

  /* eslint-disable */
  const getContainerElement = () =>
    isGroupAndRandomiseChildGroup ? (
      <div
        className={styles.groupAndRandomiseChildGroupContainer}
        ref={elementsContainerRef}
      >
        {showShuffleIcon && shuffleIconTop !== null && (
          <Icon
            type="shuffle-white"
            className={`${styles.groupAndRandomiseShuffleIcon} fade-in`}
            style={{ top: `${shuffleIconTop}px` }}
          />
        )}
        <div onClick={e => e.stopPropagation()} role="presentation">
          {elements}
        </div>
      </div>
    ) : (
      <Droppable droppableId={`grouprandomizeplaceholder-${group.id}`}>
        {provided => (
          <div {...provided.droppableProps} ref={provided.innerRef}>
            <div className={styles.dropdownPlaceholderContainer}>
              <div
                className={`${styles.dropdownPlaceholder} ${
                  styles.groupAndRandomiseChildPlaceholder
                }`}
              >
                <img
                  src={groupAndRandomiseChildPlaceholderImage}
                  alt="Drop question here"
                />
                <div className={styles.description}>
                  Choose a question block from the right and drag it into this
                  container
                </div>
              </div>
            </div>
          </div>
        )}
      </Droppable>
    );

  return (
    <div>
      <div
        className={
          isActiveGroup
            ? `${styles.groupAndRandomiseChildWrapper} ${styles.active}`
            : styles.groupAndRandomiseChildWrapper
        }
        role="presentation"
        onClick={e => {
          e.stopPropagation();

          if (!isActiveGroup) {
            actions.setActiveFlow(group);
          }
        }}
      >
        <div className={styles.childGroupInnerContainer}>
          <GroupAndRandomiseChildHeader
            setGroupNameRef={setGroupNameRef}
            group={group}
            actions={actions}
            surveyLists={state.surveyLists}
            surveyStructure={surveyStructure}
            surveyListGroup={surveyListGroup}
            expanded={expanded}
            setExpanded={setExpanded}
          />
          {expanded ? getContainerElement() : null}
        </div>
        {isActiveGroup && (
          <SidePropertiesContainer top={0} title="Edit group">
            <SideProperties
              actions={actions}
              group={group}
              surveyLists={state.surveyLists}
              surveyStructure={surveyStructure}
              groupNameRef={groupNameRef}
              surveyListGroup={surveyListGroup}
              isGroupAndRandomiseChild
            />
          </SidePropertiesContainer>
        )}
      </div>
    </div>
  );
  /* eslint-enable */
}
